import { useState } from "react";
import { useForm } from "react-hook-form";
import { HiCheckCircle, HiEye, HiEyeOff, HiLockClosed } from "react-icons/hi";
import { Link, useSearchParams } from "react-router-dom";

import { TokenResetPasswordData } from "@/types/auth";

interface ResetPasswordFormProps {
  error?: string;
  isLoading?: boolean;
  isSuccess?: boolean;
  onSubmit: (data: TokenResetPasswordData) => void;
}

export default function ResetPasswordForm({
  error,
  isLoading = false,
  isSuccess = false,
  onSubmit,
}: ResetPasswordFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<TokenResetPasswordData>({
    defaultValues: { token },
  });

  const password = watch("password");

  const handleFormSubmit = (data: TokenResetPasswordData) => {
    onSubmit(data);
  };

  if (isSuccess) {
    return (
      <div className="card w-full max-w-md bg-base-100 shadow-xl">
        <div className="card-body text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success">
              <HiCheckCircle className="h-10 w-10 text-success-content" />
            </div>
          </div>
          <h2 className="mb-4 text-2xl font-bold text-base-content">
            Password Reset Successful
          </h2>
          <p className="mb-6 text-base-content/70">
            Your password has been successfully reset. You can now sign in with
            your new password.
          </p>
          <Link className="btn btn-primary w-full" to="/auth/login">
            Continue to Login
          </Link>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="card w-full max-w-md bg-base-100 shadow-xl">
        <div className="card-body text-center">
          <div className="alert alert-error mb-4">
            <span>Invalid or missing reset token</span>
          </div>
          <p className="mb-4 text-base-content/70">
            The password reset link is invalid or has expired. Please request a
            new one.
          </p>
          <Link className="btn btn-primary w-full" to="/auth/forgot-password">
            Request New Reset Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="card w-full max-w-md bg-base-100 shadow-xl">
      <div className="card-body">
        {/* Header */}
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-base-content">
            Reset Password
          </h2>
          <p className="mt-2 text-base-content/70">
            Enter your new password below
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-error mb-4">
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Form */}
        <form className="space-y-4" onSubmit={handleSubmit(handleFormSubmit)}>
          {/* Hidden token field */}
          <input {...register("token")} type="hidden" />

          {/* Password Field */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">New Password</span>
            </label>
            <div className="relative">
              <input
                {...register("password", {
                  minLength: {
                    message: "Password must be at least 8 characters",
                    value: 8,
                  },
                  pattern: {
                    message:
                      "Password must contain uppercase, lowercase, and number",
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  },
                  required: "Password is required",
                })}
                className={`input input-bordered w-full pl-10 pr-10 ${
                  errors.password ? "input-error" : ""
                }`}
                disabled={isLoading}
                placeholder="Enter your new password"
                type={showPassword ? "text" : "password"}
              />
              <HiLockClosed className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-base-content/50" />
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 transform"
                disabled={isLoading}
                onClick={() => setShowPassword(!showPassword)}
                type="button"
              >
                {showPassword ? (
                  <HiEyeOff className="h-5 w-5 text-base-content/50" />
                ) : (
                  <HiEye className="h-5 w-5 text-base-content/50" />
                )}
              </button>
            </div>
            {errors.password && (
              <label className="label">
                <span className="label-text-alt text-error">
                  {errors.password.message}
                </span>
              </label>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Confirm New Password</span>
            </label>
            <div className="relative">
              <input
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === password || "Passwords do not match",
                })}
                className={`input input-bordered w-full pl-10 pr-10 ${
                  errors.confirmPassword ? "input-error" : ""
                }`}
                disabled={isLoading}
                placeholder="Confirm your new password"
                type={showConfirmPassword ? "text" : "password"}
              />
              <HiLockClosed className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-base-content/50" />
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 transform"
                disabled={isLoading}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                type="button"
              >
                {showConfirmPassword ? (
                  <HiEyeOff className="h-5 w-5 text-base-content/50" />
                ) : (
                  <HiEye className="h-5 w-5 text-base-content/50" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <label className="label">
                <span className="label-text-alt text-error">
                  {errors.confirmPassword.message}
                </span>
              </label>
            )}
          </div>

          {/* Password Requirements */}
          <div className="rounded-lg bg-base-200 p-3">
            <h4 className="mb-2 text-sm font-semibold">
              Password Requirements:
            </h4>
            <ul className="space-y-1 text-xs text-base-content/70">
              <li className={password?.length >= 8 ? "text-success" : ""}>
                • At least 8 characters long
              </li>
              <li
                className={/[A-Z]/.test(password || "") ? "text-success" : ""}
              >
                • Contains uppercase letter
              </li>
              <li
                className={/[a-z]/.test(password || "") ? "text-success" : ""}
              >
                • Contains lowercase letter
              </li>
              <li className={/\d/.test(password || "") ? "text-success" : ""}>
                • Contains at least one number
              </li>
            </ul>
          </div>

          {/* Submit Button */}
          <button
            className="btn btn-primary w-full"
            disabled={isLoading}
            type="submit"
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Resetting Password...
              </>
            ) : (
              "Reset Password"
            )}
          </button>
        </form>

        {/* Back to Login */}
        <div className="mt-6 text-center">
          <Link className="link link-primary text-sm" to="/auth/login">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
